/**
 * SesionPersistente — restaura la sesión web tras un refresh de la página.
 *
 * El login guarda un token firmado (session_token) en localStorage. Al montar
 * la app en una ruta protegida sin sesión en memoria, este componente lo
 * canjea en /api/sesion_rehidratar: el backend revalida al usuario y devuelve
 * los mismos datos que el login (roles, negocio, clave vigente) más un token
 * renovado. Mientras tanto se muestra un cargador para no montar pantallas
 * sin credenciales. Si el token venció o no hay, se manda al login.
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { apiPost } from "lib/api";
import {
  useMaterialUIController,
  setNombrenegocio,
  setAccesoweb,
  setRoleweb,
  setRoleDetalles,
  setModulos,
  setUsuarioLog,
  setClaveLog,
  setAnchoCodigoBarra,
  setAltoCodigoBarra,
} from "context";
import { tipoDispositivo } from "lib/dispositivo";

// Rutas públicas: login y las páginas QR de 3 segmentos (negocio/expediente/
// incauto) que se abren sin sesión. Las internas de 3 segmentos (p.ej.
// /prestamos/expediente/:noprest, /inventario/historialchasis/:chasis) sí
// requieren sesión.
function esRutaPublica(pathname) {
  if (pathname === "/login") return true;
  const segmentos = pathname.split("/").filter(Boolean);
  return segmentos.length === 3 && !["prestamos", "inventario"].includes(segmentos[0]);
}

export default function SesionPersistente({ children }) {
  const [controller, dispatch] = useMaterialUIController();
  const { usuarioLog } = controller;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const publica = esRutaPublica(pathname);
  const conSesion = Boolean(usuarioLog);

  useEffect(() => {
    if (publica || conSesion) return undefined;

    const token = localStorage.getItem("session_token");
    if (!token) {
      navigate("/login", { replace: true });
      return undefined;
    }

    let activo = true;
    apiPost("/api/sesion_rehidratar", { token, dispositivo: tipoDispositivo() })
      .then(({ ok, data }) => {
        if (!activo) return;
        if (!ok || !data?.usuarios?.length) {
          // Token vencido o usuario desactivado: la sesión guardada ya no sirve.
          localStorage.removeItem("session_token");
          navigate("/login", { replace: true });
          return;
        }
        // Mismos despachos y localStorage que hace el login.
        setClaveLog(dispatch, data.usuarios[0].clave);
        setUsuarioLog(dispatch, data.usuario);
        setNombrenegocio(dispatch, data.nombrenegocio[0].ncomp);
        setAnchoCodigoBarra(dispatch, Number(data.nombrenegocio[0].anchocodigobarra) || 0);
        setAltoCodigoBarra(dispatch, Number(data.nombrenegocio[0].altocodigobarra) || 0);
        setAccesoweb(dispatch, data.usuarios[0].web);
        setRoleweb(dispatch, data.usuarios[0].tipo);
        setRoleDetalles(dispatch, data.roles);
        setModulos(dispatch, data.modulos);
        localStorage.setItem("id", data.id);
        localStorage.setItem("sucursal", data.usuarios[0].sucursal);
        const sucUsuario = String(data.usuarios[0].sucursal ?? "").trim();
        const sucursalInfo = (data.sucursales || []).find(
          (s) => String(s.sucursal).trim() === sucUsuario
        );
        localStorage.setItem("sucursalnombre", String(sucursalInfo?.descripcion || "").trim());
        localStorage.setItem("nombrenegocio", data.nombrenegocio[0].ncomp);
        localStorage.setItem("sucursalmatricula", data.nombrenegocio[0].sucursalmatricula);
        localStorage.setItem("anchocodigobarra", Number(data.nombrenegocio[0].anchocodigobarra) || 0);
        localStorage.setItem("altocodigobarra", Number(data.nombrenegocio[0].altocodigobarra) || 0);
        if (data.session_token) localStorage.setItem("session_token", data.session_token);
      })
      .catch(() => {
        // Error de red (backend caído): se conserva el token por si el
        // problema es temporal, pero sin sesión no se puede seguir.
        if (activo) navigate("/login", { replace: true });
      });

    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publica, conSesion]);

  if (publica || conSesion) return children;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        bgcolor: "#F4F5F7",
      }}
    >
      <CircularProgress size={36} sx={{ color: "#1A2B4C" }} />
      <Typography sx={{ color: "#64748B", fontSize: "0.95rem" }}>Restaurando sesión…</Typography>
    </Box>
  );
}
